import React, { useCallback, useMemo, useState } from 'react';
import { Button, Box } from '@material-ui/core';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { toSnakeCase } from 'utils/index';
import axios from 'utils/axios';
import AttachFile from '@material-ui/icons/AttachFile';
import CloudUpload from '@material-ui/icons/CloudUpload';
import { Message } from 'semantic-ui-react';
import LinearProgress from '@material-ui/core/LinearProgress';
import { withPermissions } from 'components/HoC';
import { acceptStyle, baseStyle, focusedStyle, rejectStyle } from './md-style';
import Card from '../Card';
import './style.scss';

const MAX_ROWS = 500;

const BulkDescriptionUploadContent = () => {
  const [disabled, setDisabled] = useState(false);
  const [columnData, setColumnData] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [message, setMessage] = useState();
  const [selectedFile, setSelectedFile] = useState();
  const [uploadResult, setUploadResult] = useState();

  const uploadDescriptions = (propertyDescriptions) => {
    return axios.put('/api/admin/properties/bulk-data-upload', {
      data: propertyDescriptions,
    });
  };

  const handleFileUpload = () => {
    setDisabled(true);
    setHasError(false);
    setMessage(undefined);

    Papa.parse(selectedFile, {
      complete: async (result) => {
        try {
          if (result.data.length === 0) {
            setHasError(true);
            setMessage({ messageType: 'negative', description: 'File is empty' });
          } else if (!('property_code' in result.data[0])) {
            setHasError(true);
            setMessage({
              messageType: 'negative',
              description:
                'The file is not in the correct format. The header must be named "property code", "property_code" or "Property Code"',
            });
          } else {
            const propertyDescriptions = result.data.map((row) => ({
              property_code: row['property_code'],
              description: row['description'],
            }));

            if (propertyDescriptions.some((row) => !row.property_code)) {
              setHasError(true);
              setMessage({
                messageType: 'negative',
                description: 'The file contains rows with a missing property code',
              });
              return;
            }

            const rowsToUpload = propertyDescriptions.slice(0, MAX_ROWS);

            const resp = await uploadDescriptions(rowsToUpload);
            if (resp.data?.skipped.length > 0) {
              setMessage({ messageType: 'warning', description: 'Upload complete with errors' });
            } else {
              setMessage({ messageType: 'positive', description: 'Upload complete' });
            }
            setUploadResult(resp.data);
            setColumnData(rowsToUpload.map((row) => row.property_code));
            setSelectedFile(undefined);
          }
        } catch (error) {
          setHasError(true);
          setMessage({ messageType: 'negative', description: 'There was an error uploading the file' });
        } finally {
          setDisabled(false);
        }
      },
      header: true,
      transformHeader: (header) => toSnakeCase(header),
      skipEmptyLines: true,
    });
  };

  const onDrop = useCallback((acceptedFiles) => {
    setHasError(false);
    setMessage(undefined);
    setColumnData([]);
    setUploadResult(undefined);

    const file = acceptedFiles[0];
    setSelectedFile(file);

    Papa.parse(file, {
      complete: (result) => {
        const rowCount = result.data.length;
        if (rowCount > MAX_ROWS) {
          setMessage({
            messageType: 'warning',
            description: `This file contains ${rowCount} rows. Only the first ${MAX_ROWS} will be uploaded — the remaining ${
              rowCount - MAX_ROWS
            } rows will not be processed.`,
          });
        }
      },
      header: true,
      transformHeader: (header) => toSnakeCase(header),
      skipEmptyLines: true,
    });
  }, []);

  const downloadTemplate = () => {
    const fileUrl = 'assets/bulk_description_upload_template.csv';

    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = 'bulk_description_upload_template.csv';

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  };

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    disabled,
    onDrop,
    maxFiles: 1,
    multiple: false,
    accept: { 'text/csv': [] },
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
      ...(hasError ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject, hasError],
  );

  const renderPropertyCodes = () => {
    return (
      columnData.length > 0 && (
        <div className="file-details__container">
          <div>
            <h4>Properties Updated: {uploadResult.updated}</h4>
          </div>
          <br />
          <div className="file-details__result">
            <div>
              <h4>Property Codes Sent:</h4>
              <ul>
                {columnData.map((value, index) => (
                  <li key={index}>{value}</li>
                ))}
              </ul>
            </div>
            <div>
              {uploadResult && uploadResult.skipped?.length > 0 && (
                <>
                  <h4>Properties Skipped:</h4>
                  {uploadResult.skipped.map((property, index) => (
                    <li key={index}>
                      Property Code: {property.property_code} | Reason: {property.reason}
                    </li>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )
    );
  };

  const messageTitle =
    message?.messageType === 'positive' ? 'Success' : message?.messageType === 'warning' ? 'Notice' : 'Fail';

  return (
    <Card>
      <p>This upload can take up to 5 minutes for changes to be visible on individual properties.*</p>
      {message ? (
        <Message
          positive={message.messageType === 'positive'}
          negative={message.messageType === 'negative'}
          warning={message.messageType === 'warning'}
          onDismiss={() => {
            setMessage(undefined);
          }}
        >
          <Message.Header>{messageTitle}</Message.Header>
          <p>{message.description}</p>
        </Message>
      ) : null}

      <Box mb={2}>
        <Button variant="contained" color="primary" onClick={downloadTemplate}>
          Download template
        </Button>
      </Box>

      <div {...getRootProps({ style })}>
        <input {...getInputProps()} />
        <CloudUpload color="primary" fontSize="large" />
        <span className="drag-drop__text">Drag and drop your CSV file here to start uploading</span>
        <span className="drag-drop__text">or</span>
        <Button
          variant="contained"
          color="primary"
          classes={{ containedPrimary: 'drag-drop__button' }}
          disabled={disabled}
        >
          Browse a file
        </Button>
      </div>
      {selectedFile ? (
        <div className="file-upload">
          {disabled ? <LinearProgress /> : null}

          <div className="file-upload__text">
            <AttachFile color="primary" />
            <span>{selectedFile.name}</span>
            <Button
              variant="contained"
              color="primary"
              classes={{ containedPrimary: 'file-upload__button' }}
              disabled={disabled}
              onClick={handleFileUpload}
              style={{ marginLeft: '16px' }}
            >
              Upload
            </Button>
          </div>
        </div>
      ) : null}

      {renderPropertyCodes()}
    </Card>
  );
};

export default withPermissions(BulkDescriptionUploadContent);
